/**
 * Access & rules — who can see this archive.
 *
 * The enforcement already existed: a `ResourceGrant` on the container, checked
 * inside the search query rather than filtered afterwards. This is the surface
 * that lets an administrator actually issue one.
 */

import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconInfoCircle, IconUserPlus } from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";

import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  ARCHIVE_PERMISSIONS,
  grantArchiveAccess,
  listArchiveGrantCandidates,
  listArchiveGrants,
  revokeArchiveAccess,
  updateArchiveGrant,
  type ArchiveGrant,
  type ArchiveGrantCandidate,
  type ArchivePermission,
} from "@/shared/api/archive";
import { formatDate } from "./format";

export function ArchiveAccessTab({
  containerId,
  canManage,
}: {
  containerId: string;
  canManage: boolean;
}) {
  const getToken = useAccessToken();
  const [grants, setGrants] = useState<ArchiveGrant[] | null>(null);
  const [candidates, setCandidates] = useState<ArchiveGrantCandidate[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [permission, setPermission] = useState<ArchivePermission>("read");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const list = await listArchiveGrants(token, containerId);
      setGrants(list.items);
      if (canManage) {
        const options = await listArchiveGrantCandidates(token, containerId);
        setCandidates(options.items);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [getToken, containerId, canManage]);

  useEffect(() => {
    void load();
  }, [load]);

  async function grant() {
    if (!userId) return;
    setBusy(true);
    try {
      const token = await getToken();
      await grantArchiveAccess(token, containerId, userId, permission);
      notifications.show({
        title: "Access granted",
        message: "They can see this archive on their next request.",
        color: "teal",
      });
      setUserId(null);
      await load();
    } catch (err) {
      notifications.show({
        title: "Could not grant access",
        message: err instanceof Error ? err.message : String(err),
        color: "red",
      });
    } finally {
      setBusy(false);
    }
  }

  async function changePermission(target: string, next: ArchivePermission) {
    setBusy(true);
    try {
      const token = await getToken();
      await updateArchiveGrant(token, containerId, target, next);
      notifications.show({
        title: "Permission updated",
        message:
          next === "retrieve"
            ? "They can now open and export content from this archive."
            : "They can search and view metadata, but no longer take content out.",
        color: "teal",
      });
      await load();
    } catch (err) {
      notifications.show({
        title: "Could not change the permission",
        message: err instanceof Error ? err.message : String(err),
        color: "red",
      });
    } finally {
      setBusy(false);
    }
  }

  async function revoke(target: string) {
    if (!reason.trim()) {
      notifications.show({
        title: "A reason is required",
        message:
          "Withdrawing access to controlled records without a stated reason is not defensible in an inspection.",
        color: "orange",
      });
      return;
    }
    setBusy(true);
    try {
      const token = await getToken();
      await revokeArchiveAccess(token, containerId, target, reason.trim());
      notifications.show({
        title: "Access revoked",
        message: "Recorded in the audit trail with the reason. Effective immediately.",
        color: "teal",
      });
      setReason("");
      await load();
    } catch (err) {
      notifications.show({
        title: "Could not revoke access",
        message: err instanceof Error ? err.message : String(err),
        color: "red",
      });
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return (
      <Alert color="red" variant="light" title="Could not load access">
        {error}
      </Alert>
    );
  }
  if (!grants) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack gap="md" maw={820}>
      {/* Said rather than implied: an empty list does not mean nobody has
          access, and a reader should not have to infer that. */}
      <Alert icon={<IconInfoCircle size={18} />} variant="light" color="navy">
        Administrators have access to every archive by role. The grants below apply to Users and
        Developers, and are enforced server-side — without one, an archive is invisible.
        <Text size="xs" mt={6}>
          <strong>Read</strong> lets someone search this archive and see a record&apos;s metadata.{" "}
          <strong>Retrieve</strong> additionally lets them open documents and export rows.
        </Text>
      </Alert>

      {canManage && (
        <Paper withBorder p="md" radius="md">
          <Group align="flex-end">
            <Select
              label="Grant access to"
              placeholder={candidates.length ? "Select a person" : "Nobody left to grant"}
              flex={1}
              searchable
              disabled={!candidates.length}
              value={userId}
              data={candidates.map((c) => ({
                value: c.user_id,
                label: `${c.display_name}${c.roles.length ? ` — ${c.roles.join(", ")}` : ""}`,
              }))}
              onChange={setUserId}
            />
            <Select
              label="Permission"
              w={200}
              value={permission}
              data={[
                { value: "read", label: "Read — search and view" },
                { value: "retrieve", label: "Retrieve — also download" },
              ]}
              onChange={(value) => setPermission((value as ArchivePermission) ?? "read")}
            />
            <Button
              leftSection={<IconUserPlus size={16} />}
              disabled={!userId}
              loading={busy}
              onClick={grant}
            >
              Grant access
            </Button>
          </Group>
        </Paper>
      )}

      {grants.length === 0 ? (
        <Paper withBorder p="lg" radius="md">
          <Text fw={500}>No individual grants</Text>
          <Text size="sm" c="dimmed" mt={4}>
            Only administrators can see this archive.
          </Text>
        </Paper>
      ) : (
        <Paper withBorder radius="md">
          <Table verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Person</Table.Th>
                <Table.Th w={150}>Role</Table.Th>
                <Table.Th w={150}>Permission</Table.Th>
                <Table.Th w={140}>Granted</Table.Th>
                {canManage && <Table.Th w={110} />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {grants.map((grant) => (
                <Table.Tr key={grant.grant_id}>
                  <Table.Td>
                    <Text size="sm">{grant.display_name}</Text>
                    {grant.email && (
                      <Text size="xs" c="dimmed">
                        {grant.email}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {grant.roles.map((role) => (
                        <Badge key={role} size="sm" variant="light" tt="none">
                          {role}
                        </Badge>
                      ))}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {canManage ? (
                      <Select
                        size="xs"
                        value={grant.permission}
                        data={ARCHIVE_PERMISSIONS.map((p) => ({
                          value: p,
                          label: p === "read" ? "Read" : "Retrieve",
                        }))}
                        onChange={(value) =>
                          value && value !== grant.permission
                            ? void changePermission(grant.user_id, value as ArchivePermission)
                            : undefined
                        }
                      />
                    ) : (
                      <Text size="sm">{grant.permission}</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {formatDate(grant.granted_at_utc)}
                    </Text>
                  </Table.Td>
                  {canManage && (
                    <Table.Td>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="red"
                        loading={busy}
                        onClick={() => revoke(grant.user_id)}
                      >
                        Revoke
                      </Button>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {canManage && grants.length > 0 && (
        <TextInput
          label="Revocation reason"
          description="Required — recorded in the audit trail alongside the revocation"
          maw={460}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          placeholder="e.g. role change, project closed"
        />
      )}
    </Stack>
  );
}
